/**
 * Settings → Automations, in the shape of the Skills tab: one row an
 * automation, with Run now, Edit and the switch that turns it on or off. The
 * editor is a dialog holding what it does and when, its model and time zone,
 * its past runs, and Delete. A run is an ordinary chat; Run now opens it.
 *
 * The list is this tab's own, loaded when it opens: nothing else on the page
 * reads it. A new automation's zone is the one the browser reports.
 */

import { useEffect, useState } from 'react';
import { WEEKDAYS, type Schedule, type Weekday } from '@wire';
import {
  ApiError,
  createAutomation,
  deleteAutomation,
  listAutomations,
  listRuns,
  patchAutomation,
  runAutomation,
  type Automation,
  type AutomationList,
  type ChatSummary
} from '../../api';
import { ds } from '../../ds';
import { useRouter } from '../../router';
import { useApp } from '../../state';
import { Ph } from '../ui';
import { automationLine } from './automationText';
import { relativeTime } from './format';

const FAILED = 'That did not go through. Try again.';

const EVERY: { value: Schedule['every']; label: string }[] = [
  { value: 'hour', label: 'Every hour' },
  { value: 'day', label: 'Every day' },
  { value: 'weekdays', label: 'Weekdays' },
  { value: 'week', label: 'Chosen days' }
];

const DAY_LABELS: Record<Weekday, string> = { mon: 'Mon', tue: 'Tue', wed: 'Wed', thu: 'Thu', fri: 'Fri', sat: 'Sat', sun: 'Sun' };

const MINUTES = Array.from({ length: 12 }, (_, i) => String(i * 5).padStart(2, '0'));

const browserZone = () => Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';

const iconTile = {
  width: 32,
  height: 32,
  flex: 'none',
  borderRadius: 9,
  background: 'var(--app-panel-sunken)',
  color: 'var(--text-muted)',
  display: 'grid',
  placeItems: 'center'
} as const;

const linkButton = { fontSize: 13, color: 'var(--text-link)' } as const;

function AutomationRow({
  automation,
  busy,
  error,
  onRun,
  onEdit,
  onToggle
}: {
  automation: Automation;
  busy: boolean;
  error: string | null;
  onRun: () => void;
  onEdit: () => void;
  onToggle: (enabled: boolean) => void;
}) {
  const { Switch } = ds();
  return (
    <div style={{ display: 'flex', flexDirection: 'column', padding: '10px 16px', gap: 4, borderBottom: '1px solid var(--border-subtle)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, minHeight: 38 }}>
        <span style={iconTile}>
          <Ph name="clock-countdown" size={16} />
        </span>
        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
          <span
            style={{
              fontSize: 13.5,
              fontWeight: 600,
              color: automation.enabled ? 'var(--text-strong)' : 'var(--text-muted)',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}
          >
            {automation.name}
          </span>
          <span style={{ fontSize: 12.5, color: 'var(--text-muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {automationLine(automation)}
          </span>
        </div>
        <button type="button" className="bare hov-strong" disabled={busy} onClick={onRun} style={linkButton}>
          Run now
        </button>
        <button type="button" className="bare hov-strong" onClick={onEdit} style={linkButton}>
          Edit
        </button>
        <Switch checked={automation.enabled} disabled={busy} onChange={onToggle} />
      </div>
      {error ? <span style={{ fontSize: 12.5, color: 'var(--danger-600)', paddingLeft: 44 }}>{error}</span> : null}
    </div>
  );
}

/** The automation's runs, newest first; a click opens one. */
function PastRuns({ automationId, onOpen }: { automationId: string; onOpen: (chatId: string) => void }) {
  const [runs, setRuns] = useState<ChatSummary[] | null>(null);
  useEffect(() => {
    listRuns(automationId)
      .then((r) => setRuns(r.runs))
      .catch(() => setRuns([]));
  }, [automationId]);
  if (!runs) return null;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-strong)' }}>Past runs</span>
      {runs.length ? (
        <div style={{ display: 'flex', flexDirection: 'column', maxHeight: 180, overflowY: 'auto' }}>
          {runs.map((r) => (
            <button
              key={r.id}
              type="button"
              className="bare hov-strong"
              onClick={() => onOpen(r.id)}
              style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0', fontSize: 13, color: 'var(--text-body)', textAlign: 'left' }}
            >
              <Ph name="chat-circle" size={14} />
              <span style={{ flex: 1 }}>{relativeTime(r.updatedAt)}</span>
              {r.needsYou ? <span style={{ fontSize: 12, color: 'var(--warning-700, var(--text-muted))' }}>Waiting for you</span> : null}
            </button>
          ))}
        </div>
      ) : (
        <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>None yet.</span>
      )}
    </div>
  );
}

function AutomationDialog({
  automation,
  onSaved,
  onDeleted,
  onOpenRun,
  onClose
}: {
  automation: Automation | null;
  onSaved: (a: Automation) => void;
  onDeleted: (id: string) => void;
  onOpenRun: (chatId: string) => void;
  onClose: () => void;
}) {
  const { Dialog, Button, Input, Select, Switch } = ds();
  const { me, catalogue } = useApp();
  const models = (catalogue?.models ?? []).filter((m) => m.tools);
  const [name, setName] = useState(automation?.name ?? '');
  const [prompt, setPrompt] = useState(automation?.prompt ?? '');
  const [every, setEvery] = useState<Schedule['every']>(automation?.schedule.every ?? 'weekdays');
  const [at, setAt] = useState(automation?.schedule.at ?? '09:00');
  const [days, setDays] = useState<Weekday[]>(automation?.schedule.days ?? ['mon']);
  const [timeZone, setTimeZone] = useState(automation?.timeZone ?? me?.settings.timeZone ?? browserZone());
  const [model, setModel] = useState(automation?.model ?? (models.some((m) => m.id === me?.settings.defaultModel) ? me!.settings.defaultModel : models[0]?.id ?? ''));
  const [research, setResearch] = useState(automation?.research ?? false);
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState<{ field: string | null; message: string } | null>(null);
  const zones = Intl.supportedValuesOf('timeZone');
  const canResearch = !!me?.capabilities.webSearch && me.plan !== 'free';

  const fail = (err: unknown) => {
    const field = err instanceof ApiError && typeof err.details.field === 'string' ? err.details.field : null;
    setError({ field, message: err instanceof ApiError ? err.message : FAILED });
    setConfirming(false);
    setBusy(false);
  };

  const save = () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    const schedule: Schedule = every === 'week' ? { every, at, days } : { every, at };
    const fields = { name: name.trim(), prompt: prompt.trim(), schedule, timeZone, model, research: canResearch && research };
    (automation ? patchAutomation(automation.id, fields) : createAutomation(fields))
      .then((saved) => {
        onSaved(saved);
        onClose();
      })
      .catch(fail);
  };

  const remove = () => {
    if (!automation || busy) return;
    setBusy(true);
    deleteAutomation(automation.id)
      .then(() => {
        onDeleted(automation.id);
        onClose();
      })
      .catch(fail);
  };

  const fieldError = (field: string) => (error?.field === field ? error.message : undefined);
  const ready = !!name.trim() && !!prompt.trim() && !!model && (every !== 'week' || days.length > 0);
  const toggleDay = (d: Weekday) => setDays((now) => (now.includes(d) ? now.filter((x) => x !== d) : WEEKDAYS.filter((x) => x === d || now.includes(x))));

  return (
    <Dialog
      open
      position="fixed"
      width={560}
      title={automation ? automation.name : 'New automation'}
      onClose={onClose}
      style={{ borderRadius: 'var(--radius-dialog)', padding: 24, gap: 16, maxHeight: '100%', overflowY: 'auto' }}
      footer={
        confirming && automation ? (
          <>
            <span style={{ flex: 1, alignSelf: 'center', fontSize: 13.5, color: 'var(--text-body)' }}>Delete “{automation.name}”?</span>
            <Button variant="ghost" density="app" size="sm" disabled={busy} onClick={() => setConfirming(false)}>
              Cancel
            </Button>
            <Button variant="danger" density="app" size="sm" disabled={busy} onClick={remove}>
              Delete
            </Button>
          </>
        ) : (
          <>
            {automation ? (
              <Button variant="ghost" density="app" size="sm" disabled={busy} onClick={() => setConfirming(true)}>
                Delete
              </Button>
            ) : null}
            <span style={{ flex: 1 }} />
            <Button variant="ghost" density="app" size="sm" disabled={busy} onClick={onClose}>
              Cancel
            </Button>
            <Button variant="primary" density="app" size="sm" disabled={busy || !ready} onClick={save}>
              {automation ? 'Save' : 'Add'}
            </Button>
          </>
        )
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <Input label="Name" value={name} onChange={(e) => setName(e.target.value)} error={fieldError('name')} autoFocus={!automation} />
        <Input
          label="What to do"
          multiline
          rows={6}
          value={prompt}
          placeholder="Check my new mail and summarise what needs an answer."
          onChange={(e) => setPrompt(e.target.value)}
          error={fieldError('prompt')}
        />
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <Select
            label="How often"
            value={every}
            options={EVERY}
            onChange={(e) => setEvery(e.target.value as Schedule['every'])}
            style={{ flex: '1 1 160px' }}
          />
          {every === 'hour' ? (
            <Select
              label="Minutes past"
              value={at.slice(3)}
              options={[...new Set([...MINUTES, at.slice(3)])].sort().map((m) => ({ value: m, label: `:${m}` }))}
              onChange={(e) => setAt(`00:${e.target.value}`)}
              style={{ flex: '1 1 120px' }}
            />
          ) : (
            <Input label="Time" type="time" value={at} onChange={(e) => e.target.value && setAt(e.target.value)} style={{ flex: '1 1 120px' }} />
          )}
        </div>
        {every === 'week' ? (
          <div role="group" aria-label="Days" style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {WEEKDAYS.map((d) => {
              const on = days.includes(d);
              return (
                <button
                  key={d}
                  type="button"
                  aria-pressed={on}
                  onClick={() => toggleDay(d)}
                  className="bare"
                  style={{
                    padding: '5px 10px',
                    borderRadius: 999,
                    fontSize: 13,
                    border: `1px solid ${on ? 'transparent' : 'var(--border-subtle)'}`,
                    background: on ? 'var(--primary-soft)' : 'transparent',
                    color: on ? 'var(--primary-text)' : 'var(--text-muted)'
                  }}
                >
                  {DAY_LABELS[d]}
                </button>
              );
            })}
          </div>
        ) : null}
        {fieldError('schedule') ? <span style={{ fontSize: 12.5, color: 'var(--danger-600)' }}>{fieldError('schedule')}</span> : null}
        <Select
          label="Time zone"
          value={timeZone}
          options={[...new Set([timeZone, ...zones])].map((z) => ({ value: z, label: z.replace(/_/g, ' ') }))}
          onChange={(e) => setTimeZone(e.target.value)}
        />
        <Select label="Model" value={model} options={models.map((m) => ({ value: m.id, label: m.name }))} onChange={(e) => setModel(e.target.value)} />
        {fieldError('model') ? <span style={{ fontSize: 12.5, color: 'var(--danger-600)' }}>{fieldError('model')}</span> : null}
        {canResearch ? <Switch label="It may use Research" checked={research} onChange={setResearch} /> : null}
        {automation ? (
          <PastRuns
            automationId={automation.id}
            onOpen={(chatId) => {
              onClose();
              onOpenRun(chatId);
            }}
          />
        ) : null}
        {error && !error.field ? <span style={{ fontSize: 12.5, color: 'var(--danger-600)' }}>{error.message}</span> : null}
      </div>
    </Dialog>
  );
}

export function AutomationsTab() {
  const { Button } = ds();
  const { navigate } = useRouter();
  const [list, setList] = useState<AutomationList | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [editing, setEditing] = useState<{ automation: Automation | null } | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [rowError, setRowError] = useState<{ id: string; message: string } | null>(null);

  useEffect(() => {
    listAutomations()
      .then(setList)
      .catch(() => setLoadFailed(true));
  }, []);

  const put = (a: Automation) =>
    setList((l) => (l ? { ...l, automations: l.automations.some((x) => x.id === a.id) ? l.automations.map((x) => (x.id === a.id ? a : x)) : [...l.automations, a] } : l));

  const toggle = (a: Automation, enabled: boolean) => {
    if (busyId) return;
    setBusyId(a.id);
    setRowError(null);
    patchAutomation(a.id, { enabled })
      .then(put)
      .catch((err: unknown) => setRowError({ id: a.id, message: err instanceof ApiError ? err.message : FAILED }))
      .finally(() => setBusyId(null));
  };

  const runNow = (a: Automation) => {
    if (busyId) return;
    setBusyId(a.id);
    setRowError(null);
    runAutomation(a.id)
      .then(({ chatId }) => navigate(`/chat/${chatId}`))
      .catch((err: unknown) => setRowError({ id: a.id, message: err instanceof ApiError ? err.message : FAILED }))
      .finally(() => setBusyId(null));
  };

  if (loadFailed) return <p style={{ margin: 0, fontSize: 13.5, color: 'var(--text-muted)' }}>Automations did not load. Try again.</p>;
  if (!list) return null;
  const { automations, max } = list;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {automations.length ? (
        <div style={{ background: 'var(--surface-card)', borderRadius: 'var(--radius-app-card)', overflow: 'hidden' }}>
          {automations.map((a) => (
            <AutomationRow
              key={a.id}
              automation={a}
              busy={busyId === a.id}
              error={rowError?.id === a.id ? rowError.message : null}
              onRun={() => runNow(a)}
              onEdit={() => setEditing({ automation: a })}
              onToggle={(v) => toggle(a, v)}
            />
          ))}
        </div>
      ) : max > 0 ? (
        <p style={{ margin: 0, fontSize: 13.5, color: 'var(--text-muted)' }}>No automations yet.</p>
      ) : null}

      {max > 0 ? (
        <div>
          <Button variant="primary" density="app" size="sm" iconLeft="plus" onClick={() => setEditing({ automation: null })}>
            New automation
          </Button>
        </div>
      ) : (
        <p style={{ margin: 0, fontSize: 13.5, color: 'var(--text-muted)' }}>Automations are on Pro and Max.</p>
      )}

      {editing ? (
        <AutomationDialog
          automation={editing.automation}
          onSaved={put}
          onDeleted={(id) => setList((l) => (l ? { ...l, automations: l.automations.filter((x) => x.id !== id) } : l))}
          onOpenRun={(chatId) => navigate(`/chat/${chatId}`)}
          onClose={() => setEditing(null)}
        />
      ) : null}
    </div>
  );
}
